import { useContext, useEffect } from 'react'
import { Link, withRouter } from 'react-router-dom'
import * as H from 'history'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { PROBE_COLORS } from 'entities/pcr_run'
import { useGroupApi } from 'api/group_by'
import { useForm } from 'utils/hooks'
import { SampleSearchForm, SAMPLE_STATUS_SELECT_ITEMS } from 'entities/sample'
import moment from 'moment'
import { useMemo } from 'react'
import { Flex } from 'components/shared/flex'
import { ThemeContext } from 'contexts/theme_context'
import { useFetchInstitutionsApi } from 'api/institution'
import { InstitutionSearchForm } from 'entities/institution'
import { ScaleLoader } from 'react-spinners'
import { SampleStatusLogSearchForm, SampleStatusLogType } from 'entities/sample_status_log'
import { DateField } from 'components/shared/input'
import { FaClipboardCheck, FaRegCopy, FaUndo, FaVial } from 'react-icons/fa'
import { differenceDayJa } from 'utils/util'
import { MasterDataContext } from 'contexts/master_data_context'
import { Alert } from 'react-bootstrap'
import { AnalyticsSearchParams, useFetchAnalyticsApi } from 'api/analytics'
import { useFetchRequestFormsApi } from 'api/request_form'
import { RequestFormSearchForm } from 'entities/request_form'

type Props = {
    history: H.History
}

const DashboardPage = (props: Props) => {
    const isHomeTest = !!process.env.REACT_APP_IS_HOME_TEST
    const globalState = useContext(GlobalStateContext)
    const masterData = useContext(MasterDataContext)
    const theme = useContext(ThemeContext)
    const sampleStatusGroupSearchForm = useForm<SampleSearchForm>({
        groupBy: 'status',
        aggregate: 'Count("status")',
        aggregateAlias: 'count',
        isStandard: false,
        groupingValues: 'inspection_status,status',
        resultStatus: 'OK,REFERENCE',
    })
    const sampleStatusGroupApi = useGroupApi('samples', sampleStatusGroupSearchForm)
    const sampleDeliveryDateGroupSearchForm = useForm<SampleSearchForm>({
        groupBy: 'deadline_date',
        aggregate: 'Count("deadline_date")',
        aggregateAlias: 'count',
        sampleStatus: 'WAREHOUSED,RNA_EXTRACTED,DNA_SYNTHESIZED,PCR_READY,COMPLETED,NGS_IN_PROGRESS',
        resultStatus: 'OK,REFERENCE',
    })
    const sampleDeliveryDateGroupApi = useGroupApi('samples', sampleDeliveryDateGroupSearchForm)
    const institutionsSearchForm = useForm<InstitutionSearchForm>({ perPage: 100000 })
    const institutionsApi = useFetchInstitutionsApi(institutionsSearchForm)
    const yesterday = moment().add('day', -1)
    const sampleStatusLogSearchForm = useForm<SampleStatusLogSearchForm>({
        date: yesterday.format('YYYY-MM-DD'),
        groupBy: 'before_after',
        aggregate: 'Count("before_after")',
        aggregateAlias: 'count',
    })
    const sampleStatusLogGroupApi = useGroupApi('sample_status_logs', sampleStatusLogSearchForm)
    const analyticsSearchForm = useForm<AnalyticsSearchParams>({
        startDate: moment().add('month', -1).format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
    })
    const analyticsApi = useFetchAnalyticsApi(analyticsSearchForm)

    const stillInterviewRequestFormsForm = useForm<RequestFormSearchForm>({ transportationReportIdIsNull: false, sampleStatus: 'INTERVIEWED' })
    const stillInspectRequestFormsApi = useFetchRequestFormsApi(stillInterviewRequestFormsForm)

    useEffect(() => {
        sampleStatusGroupApi.execute()
        sampleDeliveryDateGroupApi.execute()
        institutionsApi.execute()
        sampleStatusLogGroupApi.execute()
        masterData.reloadTaskSets()
        analyticsApi.execute()
        stillInspectRequestFormsApi.execute()
    }, [])

    const sampleStatusGroupData = useMemo(() => {
        return SAMPLE_STATUS_SELECT_ITEMS.filter((ss) => ss.value !== 'SENT' && ss.value !== 'DISPOSED' && ss.value !== 'PRINT_REQUESTED').map((selectItem) => {
            const status = selectItem.value
            const obj = sampleStatusGroupApi.response.groups?.filter((obj) => {
                return obj.status === status
            })
            const label = SAMPLE_STATUS_SELECT_ITEMS.find((ss) => ss.value === status)?.label

            let url = ''
            if (status === 'WAREHOUSED') {
                url = '/samples?is_standard=false'
            } else if (status === 'RNA_EXTRACTED') {
                url = '/extractions?is_standard=false'
            } else if (status === 'DNA_SYNTHESIZED') {
                url = '/dna?is_standard=false'
            } else if (status === 'PCR_READY') {
                url = '/pcr_runs?is_standard=false'
            } else if (status === 'COMPLETED') {
                url = '/print_requests?is_standard=false'
            } else if (status === 'PRINT_REQUESTED') {
                url = '/send_histories?is_standard=false'
            } else if (status === 'RECOLLECT') {
                url = '/samples?sampleStatus=RECOLLECT&ordering=-sample_priority%2Csample_deadline_date&listSerializer=true'
            }

            const baseData = { name: label, count: obj.reduce((sum, o) => sum + o.count, 0), url: url }
            if (obj.length === 0) {
                return baseData
            }
            return baseData
        })
    }, [sampleStatusGroupApi.loading])

    const sampleDeliveryDateGroupData = useMemo(() => {
        const data: {
            label: string
            count: number
            color: string
            condition: number
            className?: string
            deadlineDateAfter?: string
            deadlineDateBefore?: string
        }[] = [
            { label: '期限切れ', count: 0, color: theme.danger, condition: 0, className: 'flashing' },
            { label: '残り1日', count: 0, color: theme.danger, condition: 1 },
            { label: '残り2日', count: 0, color: theme.warning, condition: 2 },
            { label: '残り3日', count: 0, color: theme.warning, condition: 3 },
            { label: '残り4日', count: 0, color: theme.success, condition: 4 },
            { label: '残り5日', count: 0, color: theme.success, condition: 5 },
            { label: '6日以上', count: 0, color: theme.success, condition: 6 },
        ]
        sampleDeliveryDateGroupApi.response.groups.forEach((group) => {
            if (!group.deadlineDate) return
            const date = moment(group.deadlineDate)
            const today = moment(moment().format('YYYY-MM-DD'))
            const daysRemaining = date.diff(today, 'days')

            for (let i = 0; i < data.length; i++) {
                const d = data[i]
                if (d.condition >= daysRemaining) {
                    d.count += group.count
                    if (d.condition === 0) {
                        d.deadlineDateBefore = today.format('YYYY-MM-DD')
                    } else if (d.condition === 6) {
                        d.deadlineDateAfter = today.add('day', 6).format('YYYY-MM-DD')
                    } else {
                        d.deadlineDateBefore = group.deadlineDate
                        d.deadlineDateAfter = group.deadlineDate
                    }
                    break
                }
            }
        })
        return data.reverse()
    }, [sampleDeliveryDateGroupApi.loading])

    type RemainingPercentGroup = {
        name: string
        count: number
        condition: number
        color: string
        institutionIds: number[]
    }
    const remainingPercentGroupData = useMemo(() => {
        const groups: RemainingPercentGroup[] = [
            { name: '残25~50%', count: 0, condition: 25, color: theme.warning, institutionIds: [] },
            { name: '残25%以下', count: 0, condition: -1, color: theme.danger, institutionIds: [] },
        ]
        institutionsApi.response.results
            .filter((institution) => {
                return institution.isAutoShipping
            })
            .forEach((institution) => {
                const nRemain = institution.remainingRequestForm || 0
                const nForm = institution.scanSendFormCount || 1
                const percent = (nRemain / (nForm * 1.0)) * 100.0
                for (let i = 0; i < groups.length; i++) {
                    const group = groups[i]
                    if (50 < percent) {
                        break
                    }
                    if (group.condition < percent) {
                        group.count += 1
                        group.institutionIds.push(institution.id!)
                        break
                    }
                }
            })
        return groups.reverse()
    }, [institutionsApi.loading])

    const headerItem = (title: string) => {
        return (
            <div
                className="shadow"
                style={{
                    fontSize: 18,
                    marginBottom: 10,
                    position: 'absolute',
                    top: -40,
                    left: -10,
                    border: `2px solid ${theme.primary}`,
                    color: theme.primary,
                    padding: '10px 40px',
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}
            >
                {title}
            </div>
        )
    }

    const loadingView = (loading: boolean) => {
        if (loading) {
            return (
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    style={{ backgroundColor: '#1111', position: 'absolute', width: '100%', height: '100%', top: 0, left: 0 }}
                >
                    <ScaleLoader color={theme.brand} />
                </Flex>
            )
        }
    }

    const getLogCount = (status: SampleStatusLogType): number => {
        const groups = sampleStatusLogGroupApi.response.groups
        const group = groups.find((g) => g.beforeAfter === status)
        return group?.count || 0
    }

    const hex2rgba = (hex: string, alpha = 1) => {
        let r = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)
        let c = null
        if (r) {
            c = r.slice(1, 4).map(function (x) {
                return parseInt(x, 16)
            })
        }
        r = hex.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i)
        if (r) {
            c = r.slice(1, 4).map(function (x) {
                return 0x11 * parseInt(x, 16)
            })
        }
        // 該当しない場合は、nullを返す.
        if (!c) {
            return undefined
        }
        return `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alpha})`
    }

    const CustomTooltip = ({ active, payload, label }: any): JSX.Element => {
        if (active && payload && payload.length) {
            return (
                <Flex
                    style={{ backgroundColor: 'white', opacity: 0.8, color: '#444', padding: 10, border: '1px solid', borderColor: '#ccc' }}
                    flexDirection="column"
                    className=""
                >
                    <div style={{ fontWeight: 'bold' }}>{`${label} : ${(payload as any[]).reduce((before: number, p: any) => p.value + before, 0)}件`}</div>
                    {(payload as any[])
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                        .filter((p: any) => p.value !== 0)
                        .map((p: any) => {
                            const index = sampleStatusGroupData.map((d) => d.name).indexOf(label)
                            const baseColor = PROBE_COLORS[index % PROBE_COLORS.length]
                            return (
                                <Flex key={p.name} className="" alignItems="center">
                                    <div style={{ backgroundColor: baseColor, minWidth: 15, minHeight: 15, marginRight: 5 }} />
                                    <div>{`${p.value}件`}</div>
                                </Flex>
                            )
                        })}
                </Flex>
            )
        } else return <></>
    }
    const YieldCustomTooltip = ({ active, payload, label }: any): JSX.Element => {
        if (active && payload && payload.length) {
            return (
                <div style={{ backgroundColor: 'white', opacity: 0.8, color: '#444', padding: 10, border: '1px solid', borderColor: '#ccc' }}>
                    {payload[0].payload.name}: {payload[0].value}件
                </div>
            )
        } else {
            return <></>
        }
    }

    const COLORS = ['#0088FE', '#FFBB28', '#00C49F', '#FF8042']

    return (
        <>
            <div style={{ marginBottom: 60, width: '100%', marginTop: 10 }}>
                {stillInspectRequestFormsApi.response.results.length > 0 && (
                    <Alert>
                        仮検品したサンプルのWEB問診が完了しています。
                        <Link
                            to={
                                'inspections/transportation_reports/receipt?ids=' +
                                stillInspectRequestFormsApi.response.results.map((r) => r.transportationReportId)
                            }
                        >
                            こちら
                        </Link>
                        から検品してください
                    </Alert>
                )}
                {masterData.taskSets.length > 0 && (
                    <Alert variant="warning">
                        <Link to="task_sets?incomplete=true&kind=MATERIAL">やることリスト</Link> に「
                        {masterData.taskSets.filter((ts) => ts.kind === 'MATERIAL').length}件」の資材発送タスクがあります
                    </Alert>
                )}

                <Flex style={{ height: 50 }}>
                    <div style={{ marginTop: 5 }}>TAT・歩留集計期間</div>
                    <div style={{ width: 120 }}>
                        <DateField
                            form={analyticsSearchForm}
                            attr="startDate"
                            defaultDate={new Date(analyticsSearchForm.object.startDate!)}
                            maxDate={new Date()}
                            hideResetButton
                        />
                    </div>
                    <div style={{ marginTop: 5 }}>~</div>
                    <div style={{ width: 120 }}>
                        <DateField
                            form={analyticsSearchForm}
                            attr="endDate"
                            defaultDate={new Date(analyticsSearchForm.object.endDate!)}
                            maxDate={new Date()}
                            hideResetButton
                        />
                    </div>
                </Flex>
                <Flex style={{ marginBottom: 20 }}>
                    <Flex style={{ flexGrow: 1, height: 300 }} className="shadow" flexDirection="column">
                        <Flex flexDirection="column" style={{ width: '100%' }}>
                            <div
                                style={{
                                    padding: 10,
                                    textAlign: 'center',
                                    width: '100%',
                                    backgroundColor: '#0088FE',
                                    fontSize: 22,
                                    color: 'white',
                                    flexGrow: 1,
                                }}
                            >
                                平均TAT
                                <div style={{ fontSize: 14 }}>（TAT = 検体受付日～結果が出た日）</div>
                            </div>
                            <Flex
                                style={{ flexGrow: 1, padding: 10, fontSize: 80, width: '100%' }}
                                justifyContent="center"
                                alignItems="center"
                                flexDirection="column"
                            >
                                <div style={{ position: 'relative', lineHeight: 0.9 }}>
                                    {analyticsApi.response.avgTat || '-'}
                                    <span style={{ fontSize: 40, position: 'absolute', bottom: 15, right: -45 }}>日</span>
                                </div>

                                <div style={{ fontSize: 14 }}>
                                    ({analyticsSearchForm.object.startDate} ~ {analyticsSearchForm.object.endDate})
                                </div>
                            </Flex>
                        </Flex>
                        <div style={{ width: '100%', padding: 10 }}>
                            <table style={{ width: '100%', fontSize: 14 }}>
                                <tbody>
                                    <tr>
                                        <td>1検体あたりの平均精製数</td>
                                        <td style={{ textAlign: 'center' }}>{analyticsApi.response.avgCount?.avgExtraction?.toFixed(1) || '-'} 回</td>
                                    </tr>
                                    <tr>
                                        <td>1検体あたりの平均合成数</td>
                                        <td style={{ textAlign: 'center' }}>{analyticsApi.response.avgCount?.avgDna?.toFixed(1) || '-'} 回</td>
                                    </tr>
                                    <tr>
                                        <td>1検体あたりの平均測定数</td>
                                        <td style={{ textAlign: 'center' }}>{analyticsApi.response.avgCount?.avgPcr?.toFixed(1) || '-'} 回</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </Flex>
                    <div style={{ width: '30%', height: 300, padding: 10 }} className="shadow">
                        <div style={{ fontSize: 15, color: '#666', textAlign: 'center' }}>
                            歩留(合計{analyticsApi.response.yieldCount?.map((v) => v.value).reduce((c, b) => c + b)}測定)
                            <div style={{ fontSize: 14 }}>
                                ({analyticsSearchForm.object.startDate} ~ {analyticsSearchForm.object.endDate})
                            </div>
                        </div>
                        {analyticsApi.response.yieldCount?.length !== 0 && (
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart width={600} height={250}>
                                    <Pie
                                        data={analyticsApi.response.yieldCount}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={100}
                                        // fill="#8884d8"
                                        dataKey="value"
                                        nameKey="name"
                                        label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
                                            const RADIAN = Math.PI / 180
                                            const radius = innerRadius + (outerRadius - innerRadius) * 0.5
                                            const x = cx + radius * Math.cos(-midAngle * RADIAN)
                                            const y = cy + radius * Math.sin(-midAngle * RADIAN)
                                            if (percent == 0) {
                                                return <></>
                                            }
                                            return (
                                                <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                                                    {`${(percent * 100).toFixed(0)}%`}
                                                </text>
                                            )
                                        }}
                                        isAnimationActive={false}
                                    >
                                        {/* <LabelList dataKey="name" /> */}
                                        {analyticsApi.response.yieldCount?.map((entry, index) => {
                                            return <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        })}
                                    </Pie>
                                    <Legend></Legend>
                                    {/* <Tooltip></Tooltip> */}
                                    <Tooltip content={<YieldCustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {!isHomeTest && (
                        <div className="shadow" style={{ width: '30%', height: 300, position: 'relative', padding: 10 }}>
                            {loadingView(institutionsApi.loading)}
                            <div style={{ fontSize: 15, color: '#666', marginBottom: 10, textAlign: 'center', marginTop: 10 }}>資材（SCAN）残り割合別病院数</div>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={remainingPercentGroupData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" height={60} tick={{ fontSize: 14 }} interval={0} />
                                    <YAxis tickSize={1} />
                                    <Tooltip />
                                    {/* <Legend /> */}
                                    <Bar dataKey="count">
                                        <LabelList
                                            dataKey="count"
                                            position="inside"
                                            fill="#fff"
                                            formatter={(name: string) => {
                                                return name + '病院'
                                            }}
                                        />
                                        {remainingPercentGroupData.map((data, index) => (
                                            <Cell
                                                key={`cell-status-${index}`}
                                                fill={data.color}
                                                onClick={() => {
                                                    props.history.push('/institutions?ordering=remaining_percent&id=' + data.institutionIds.join(','))
                                                }}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </Flex>
                <Flex style={{ height: 50 }}>
                    <div style={{ width: 120 }}>
                        <DateField form={sampleStatusLogSearchForm} attr="date" defaultDate={yesterday.toDate()} maxDate={new Date()} hideResetButton />
                    </div>
                    <div style={{ marginTop: 6, fontSize: 18, color: '#666' }}>{differenceDayJa(moment(sampleStatusLogSearchForm.object.date))}の作業日報</div>
                </Flex>
                <Flex style={{ width: '100%', position: 'relative' }}>
                    {loadingView(sampleStatusLogGroupApi.loading)}
                    <DayReport
                        title="検品作業"
                        items={[
                            { title: '入庫', count: getLogCount('None__DRAFT') },
                            { title: '入力', count: getLogCount('DRAFT__INPUTTED') },
                            { title: '確認', count: getLogCount('INPUTTED__INSPECTED') },
                        ]}
                        color={theme.red}
                        icon={<FaClipboardCheck color="white" />}
                    />
                    <DayReport
                        title="検査作業"
                        items={[
                            { title: 'RNA精製', count: getLogCount('WAREHOUSED__RNA_EXTRACTED') },
                            { title: 'cDNA合成', count: getLogCount('RNA_EXTRACTED__DNA_SYNTHESIZED') },
                            { title: 'qPCR Run', count: getLogCount('PCR_READY__COMPLETED') },
                        ]}
                        color={theme.green}
                        icon={<FaVial color="white" />}
                    />
                    <DayReport
                        title="送付作業"
                        items={[{ title: '結果表送付', count: getLogCount('COMPLETED__SENT') }]}
                        color={theme.blue3}
                        icon={<FaRegCopy color="white" />}
                    />
                    <DayReport
                        title="再検査"
                        items={[
                            { title: 'RNA再精製', count: getLogCount('PCR_READY__WAREHOUSED') },
                            { title: 'cDNA再合成', count: getLogCount('PCR_READY__RNA_EXTRACTED') },
                            { title: 'qPCR再測定', count: getLogCount('PCR_READY__DNA_SYNTHESIZED') },
                        ]}
                        color={theme.yellow}
                        icon={<FaUndo color="white"></FaUndo>}
                    />
                </Flex>
            </div>
            <div className="shadow" style={{ marginBottom: 20, padding: 10, width: '100%', position: 'relative', marginTop: 40 }}>
                {loadingView(sampleStatusGroupApi.loading || sampleDeliveryDateGroupApi.loading)}
                {headerItem('検査状況')}
                <Flex style={{ width: '100%' }}>
                    <div style={{ width: '100%' }}>
                        <div style={{ fontSize: 15, color: '#666', marginBottom: 10, textAlign: 'center' }}>ステータス別検体数</div>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={sampleStatusGroupData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={-25} textAnchor="end" height={60} tick={{ fontSize: 14 }} interval={0} />
                                <YAxis label={{ value: '検体数', angle: -90, position: 'insideLeft', fill: '#888' }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" stackId={'x'}>
                                    <LabelList dataKey="count" position="inside" fill="#fff" />
                                    {sampleStatusGroupData.map((data, index) => (
                                        <Cell
                                            key={`cell-status-${index}`}
                                            fill={hex2rgba(PROBE_COLORS[index % PROBE_COLORS.length])}
                                            onClick={() => {
                                                props.history.push(data.url)
                                            }}
                                        />
                                    ))}
                                </Bar>
                                {/* <Bar dataKey="inputted" stackId={'x'}>
                                    <LabelList dataKey="count" position="inside" fill="#fff" />
                                    {sampleStatusGroupData.map((data, index) => (
                                        <Cell
                                            key={`cell-status-${index}`}
                                            fill={hex2rgba(PROBE_COLORS[index % PROBE_COLORS.length], 0.5)}
                                            onClick={() => {
                                                props.history.push(data.url)
                                            }}
                                        />
                                    ))}
                                </Bar>
                                <Bar dataKey="draft" stackId={'x'}>
                                    <LabelList dataKey="count" position="inside" fill="#fff" />
                                    {sampleStatusGroupData.map((data, index) => (
                                        <Cell
                                            key={`cell-status-${index}`}
                                            fill={hex2rgba(PROBE_COLORS[index % PROBE_COLORS.length], 0.2)}
                                            onClick={() => {
                                                props.history.push(data.url)
                                            }}
                                        />
                                    ))}
                                </Bar> */}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div style={{ width: '100%' }}>
                        <div style={{ fontSize: 15, color: '#666', marginBottom: 10, textAlign: 'center' }}>検体到着日からの経過日数別検体数</div>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={sampleDeliveryDateGroupData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" angle={-25} textAnchor="end" height={60} tick={{ fontSize: 14 }} interval={0} />
                                <YAxis />
                                <Tooltip />
                                {/* <Legend /> */}
                                <Bar dataKey="count" fill="#0080c8">
                                    <LabelList dataKey="count" position="inside" fill="#fff" />

                                    {sampleDeliveryDateGroupData.map((data, index) => (
                                        <Cell
                                            key={`cell-past-${index}`}
                                            fill={data.color}
                                            className={data.className}
                                            onClick={() => {
                                                props.history.push(
                                                    `/samples?sampleStatus=WAREHOUSED,RNA_EXTRACTED,DNA_SYNTHESIZED,PCR_READY,COMPLETED,PRINT_REQUESTED&${[
                                                        data.deadlineDateAfter ? 'deadline_date_after=' + data.deadlineDateAfter : undefined,
                                                        data.deadlineDateBefore ? 'deadline_date_before=' + data.deadlineDateBefore : undefined,
                                                    ]
                                                        .filter((d) => !!d)
                                                        .join('&')}`
                                                )
                                            }}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Flex>
            </div>
            <Flex style={{ marginTop: 60 }}></Flex>
        </>
    )
}

const DayReport = ({ title, items, color, icon }: { title: string; items: { title: string; count: number }[]; color: string; icon: JSX.Element }) => {
    return (
        <Flex style={{ width: '100%' }}>
            <div className="shadow" style={{ padding: 10, width: '100%', color: '#666', height: 180 }}>
                <Flex
                    justifyContent="center"
                    className="shadow"
                    style={{
                        fontSize: 18,
                        marginBottom: 10,
                        backgroundColor: color,
                        borderRadius: 3,
                        padding: 10,
                        color: 'white',
                        textAlign: 'center',
                    }}
                >
                    <Flex alignItems="center">
                        <div style={{ marginRight: 10 }}>{icon}</div>
                        <div>{title}</div>
                    </Flex>
                </Flex>
                <div style={{ padding: 10 }}>
                    {items.map((item, index) => {
                        return (
                            <Flex key={'day-report-' + item.title} justifyContent="space-between" style={{ marginBottom: 5 }}>
                                <div>{item.title}</div>
                                <div>{item.count} 件</div>
                            </Flex>
                        )
                    })}
                </div>
            </div>
        </Flex>
    )
}

export default withRouter(DashboardPage)
